import { z } from "zod";
import type { DemoStep, DemoStepProgress } from "./hostedDemo.js";

export const DEMO_NAVIGATION_STEPS = ["projects", "project", "entity", "view", "node"] as const;
export const DemoNavigationStepSchema = z.enum(DEMO_NAVIGATION_STEPS);
export type DemoNavigationStep = z.infer<typeof DemoNavigationStepSchema>;
export type DemoTutorialStage = DemoNavigationStep | "edit" | "save" | "reload" | "use" | "completed";
export const DemoObservationSchema = z.object({
	path: z.string().max(512),
	projectId: z.string().max(128).optional(),
	entityId: z.string().max(128).optional(),
	view: z.enum(["form", "graph"]).optional(),
	nodeId: z.string().max(128).optional(),
	visible: z.boolean().optional(),
	expanded: z.boolean().optional(),
}).strict();
export type DemoObservation = z.infer<typeof DemoObservationSchema>;

export function demoNavigationComplete(progress: DemoStepProgress): boolean {
	return DEMO_NAVIGATION_STEPS.every(step => Boolean(progress.learning?.[step]));
}

export function demoTutorialStage(progress: DemoStepProgress, draftChanged: boolean, loadedEdit: string | null): DemoTutorialStage {
	if (progress.completedAt) return draftChanged ? "save" : "completed";
	const navigation = DEMO_NAVIGATION_STEPS.find(step => !progress.learning?.[step]);
	if (navigation) return navigation;
	if (draftChanged) return "save";
	if (!progress.savedAt || !progress.editId) return "edit";
	return loadedEdit === progress.editId ? "use" : "reload";
}

export function matchesDemoObservation(
	stage: DemoNavigationStep,
	observation: DemoObservation,
	target: { projectId: string; slug: string; entityId: string; nodeId?: string },
	chapter: DemoStep,
): boolean {
	const base = `/projects/${target.slug}`;
	const inProject = observation.projectId === target.projectId && (observation.path === base || observation.path.startsWith(`${base}/`));
	if (stage === "projects") return (observation.path === "/dashboard" && observation.visible === true) || (chapter !== "dialogue" && inProject);
	if (!inProject) return false;
	if (stage === "project") return true;
	if (observation.entityId !== target.entityId || observation.path !== `${base}/${chapter === "shop" ? "recipes" : "items"}/${target.entityId}`) return false;
	if (stage === "entity") return true;
	if (observation.view !== (chapter === "shop" ? "form" : "graph")) return false;
	if (stage === "view") return observation.visible === true;
	return observation.visible === true && observation.expanded === true &&
		(chapter === "shop" || observation.nodeId === target.nodeId);
}

function record(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

export function demoEditableValue(entity: unknown, step: DemoStep, nodeId?: string): unknown {
	const entry = record(entity);
	if (step === "shop") {
		if (entry?.developerProductId || !Array.isArray(entry?.inputs) || entry.inputs.length !== 1) return null;
		const input = record(entry.inputs[0]);
		return input?.type === "currency" && typeof input.amount === "number" && input.amount > 0
			? { currencyId: input.currencyId, amount: input.amount } : null;
	}
	const node = record(record(record(entry?.graph)?.nodes)?.[nodeId ?? ""]);
	if (step === "dialogue") {
		if (node?.type !== "Dialogue" || !Array.isArray(node.lines)) return null;
		const line = record(node.lines[0]);
		return typeof line?.text === "string" && line.text.trim() ? line.text : null;
	}
	return node?.type === "ModifyItem" && node.mode === "Add" && !node.spawnDrop && typeof node.itemId === "string" ? node.itemId : null;
}
